import gsap from 'gsap';
import { Mesh, Object3D, PerspectiveCamera, ShaderMaterial, Vector2, Vector3 } from "three";

import CameraControls from "camera-controls";
import { isDesktop, isMobile } from "utils/platform";
import { ScenesManager } from "./ScenesManager";
import { APP_EVENTS, EventsManager } from 'webgl/events/EventsManager';
import { normalizePointerTo2D } from './utils/normalize-pointer';
import { delay } from 'utils/time';
export class CameraManager {
  static controls: CameraControls;
  static _centerPosition = new Vector3();
  static _normal = new Vector3();
  static _cameraPosition = new Vector3();
  static _sizes = new Vector3();
  static initialPosition = new Vector3(6.721296366697009, -0.7478038039009569, 0.04097793110084611 );
  static initialTarget = new Vector3(0, 0, 0.04787249704082751);
  static interiorPositionTelas = new Vector3(0.21745011070655673, 0.8921259632990847, -2.1416161392038355);
  static interiorTargetTelas = new Vector3(0.032512754595757656, 0.5074881237619971, 0 );
  static interiorPosition = new Vector3(-3.395894308900664, 0.17471084993688624, -0.6207127298464026);
  static interiorTarget = new Vector3(0.032512754595757656, 0.5074881237619971, 0 );
  static featureStoredPosition = new Vector3();
  static featureStoredLookAt = new Vector3(0,0,0);
  static playingIntro = false;
  static isRotating = true;
  static isRotatingTimer: ReturnType<typeof setTimeout>;
  static currentRotation: Vector2;
  static isZooming = false;
  static interiorRotateSpeed = 0.3;

  static setup(camera: PerspectiveCamera, canvas: HTMLCanvasElement) {
    if (CameraManager.controls) return;
    CameraManager.controls = new CameraControls(camera, canvas);
    if (!ScenesManager.freeCamera) {
      CameraManager.controls.maxPolarAngle = isDesktop() ? 1.6452097969841715 : 1.5900083881221385;
      CameraManager.controls.minDistance = isDesktop() ? 3 : 6;
      CameraManager.controls.maxDistance = isDesktop() ? 10 : 16;
      CameraManager.controls.draggingSmoothTime = 0.02;
      CameraManager.controls.mouseButtons.middle = CameraControls.ACTION.NONE;
      CameraManager.controls.mouseButtons.right = CameraControls.ACTION.NONE;
    }

    CameraManager.controls.addEventListener('controlstart', () => {
      clearTimeout(CameraManager.isRotatingTimer);
      CameraManager.isRotatingTimer = setTimeout(() => {
        clearTimeout(CameraManager.isRotatingTimer);
        CameraManager.isRotating = true;
        if (isMobile()) {          
          CameraManager.toggleTitle(0);
        }
      }, 300);
    });
    CameraManager.controls.addEventListener('controlend', () => {
      clearTimeout(CameraManager.isRotatingTimer);
      CameraManager.isRotatingTimer = setTimeout(() => {
        clearTimeout(CameraManager.isRotatingTimer);
        CameraManager.isRotating = false;
        if (isMobile()) {
          CameraManager.toggleTitle(1);
        }
      }, 500);
    });
    CameraManager.controls.addEventListener('update', () => {
      if (!CameraManager.currentRotation) {
        CameraManager.currentRotation = new Vector2(0,0);
      }
      CameraManager.currentRotation.set(CameraManager.controls.azimuthAngle, CameraManager.controls.polarAngle);
    });

    if (isMobile()) {
      this.initialPosition.set(21.4602617827068, 0.5308041905497143, -0.03921722550979043 );
    }

    CameraManager.goToInitial();
    
    /**
     * 
     * use this function to get camera coordinates, then copy from the console and add to features.ts
     */
    window.getCameraCoordinates = () => CameraManager.getCurrentPosAndTarget(true);
  }

  static toggleTitle(opacity: number) {
    if (ScenesManager.inFeature) return;
    const titleComponentID = document.getElementById('titleComponentID');
    if (titleComponentID) {
      titleComponentID.style.opacity = `${opacity}`;
    }
  }

  static setEnable(val: boolean) {
    if (CameraManager.controls) {
      CameraManager.controls.enabled = val;
    }
  }

  static enabled() {
    return CameraManager.controls.enabled;
  }

  static async zoom(zoomIn: boolean) {
    CameraManager.isZooming = true;
    let middleDistance = 7;
    let desiredDistance = zoomIn ? 3 : 9;
    if (ScenesManager.inInterior) {
      desiredDistance = zoomIn ? 0.8 : 2.2;
      middleDistance = 1.5;
    }
    if (zoomIn && CameraManager.controls.distance > middleDistance) {
      desiredDistance = middleDistance;
    }
    if (!zoomIn && CameraManager.controls.distance < middleDistance) {
      desiredDistance = middleDistance;
    }
    
    await CameraManager.controls.dollyTo(desiredDistance, true);

    CameraManager.isZooming = false;
  }
  
  static async rotate(horizontal: boolean, direction: number) {
    CameraManager.isRotating = true;
    const rotAmount = Math.PI * 0.25;
    if (horizontal) {
      if (direction > 0) {
        if (!ScenesManager.inInterior) {
          CameraManager.currentRotation.y += rotAmount;
        } else {
          CameraManager.currentRotation.y -= rotAmount;
        }
        if (CameraManager.currentRotation.y > 1.6452097969841715) {
          CameraManager.currentRotation.y = 1.6452097969841715;
        }
      } else {
        if (!ScenesManager.inInterior) {
          CameraManager.currentRotation.y -= rotAmount;
        } else {
          CameraManager.currentRotation.y += rotAmount;
        }
      }
    } else {
      if (direction > 0) {
        if (!ScenesManager.inInterior) {
          CameraManager.currentRotation.x += rotAmount;
        } else {
          CameraManager.currentRotation.x -= rotAmount;
        }
      } else {
        if (!ScenesManager.inInterior) {
          CameraManager.currentRotation.x -= rotAmount;
        } else {
          CameraManager.currentRotation.x += rotAmount;
        }
      }
    }

    gsap.to(CameraManager.controls, {
      azimuthAngle:CameraManager.currentRotation.x,
      polarAngle: CameraManager.currentRotation.y,
      durattion: 1.4,
    })

    CameraManager.isRotating = false;
    
  }

  static async goToInitial() {
    await CameraManager.controls.setLookAt(
      CameraManager.initialPosition.x, CameraManager.initialPosition.y, CameraManager.initialPosition.z,
      CameraManager.initialTarget.x, CameraManager.initialTarget.y, CameraManager.initialTarget.z,
      true,
    );
    ScenesManager.inFeature = false;
    CameraManager.setEnable(true);
    if (!CameraManager.currentRotation) {
      CameraManager.currentRotation = new Vector2(0,0);
    }
    CameraManager.currentRotation.set(CameraManager.controls.azimuthAngle, CameraManager.controls.polarAngle);
  }
  static playIntro() {
    CameraManager.playingIntro = true;
    if (isMobile()) {
      CameraManager.controls.setLookAt(
        5.865342412895533, 2.080469495261985, 12.728587565985617 ,
        -0.1752815410761034, -0.07006796836563967, 0,
        true,
      );
    } else {
      CameraManager.controls.setLookAt(
        2.8067791506598523, 1.6357862148623452, 5.925702540999585 ,
        0, 0, 0,  
        true,
      );
    }
  }

  static async goToFeature(to: Vector3, lookAt: Vector3, target?: Object3D) {
    if (ScenesManager.hasOverlay || CameraManager.playingIntro || CameraManager.isRotating || CameraManager.isZooming) return;
    if (isMobile()) {
      CameraManager.toggleTitle(0);
    }


    switch (ScenesManager.currentFeatureName) {
      case "rodas":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'roda',
        })
        break;
      case "assinaturaDeDesign":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'design',
        })
      break;
      case "tetoSolar":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'teto',
        })
      break;
      case "farolGrade":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'farol_grade',
        })
      break;
      case "lanternaTraseira":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'farol_traseiro',
        })
      break;
      case "soleira":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'soleira',
        })
      break;
      case "coluna":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'coluna',
        })
      break;
      case "retrovisor":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'retrovisor',
      })
      break;
      case "biTone":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'cor-bi-tone',
        })
      break;
      case "telas":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'tela',
        })
      break;
      case "somBose":
        window.dataLayer.push({
          event: 'clique_navegacao',
          interacao: 'som_bose',
        })
      break;
    
      default:
        break;
    }

    ScenesManager.inFeature = true;
    CameraManager.getCurrentPosAndTarget();
    await CameraManager.controls.setLookAt(
      to.x, to.y, to.z,
      lookAt.x, lookAt.y, lookAt.z,
      true,
    );
    CameraManager.setEnable(false);

    if (target) {
      const coords2D = normalizePointerTo2D(
        target,
        ScenesManager.mainScene.camera,
        window.innerWidth,
        window.innerHeight,
      )
      ScenesManager.currentFeature2D.set(coords2D.x, coords2D.y)
      // EnvironmentManager.updateFeatureLight({
      //   pos: to,
      //   lookAt: target.position,
      //   intensity: 100,
      // })
    }
    for (let key in ScenesManager.featureTravelingParticles) {
      if (ScenesManager.featureTravelingParticles[key]) {
        ScenesManager.featureTravelingParticles[key].targets.forEach(item => {
          gsap.to((item.material as ShaderMaterial).uniforms.uOpacity, {
            value: 0.0,
            ease: "quad.inOut",
            duration: 0.8,
            onComplete: () => {
              item.visible = false
            }
          });
        })
      }
    }
    EventsManager.instance.trigger(APP_EVENTS.FEATURE_SHOW);
   
    
  }

  static async goFromFeature() {    
    // EnvironmentManager.hideFeatureLight();
    EventsManager.instance.trigger(APP_EVENTS.FEATURE_HIDE);
    CameraManager.controls.setPosition(CameraManager.featureStoredPosition.x, CameraManager.featureStoredPosition.y, CameraManager.featureStoredPosition.z, true);
    await CameraManager.controls.setTarget(CameraManager.featureStoredLookAt.x, CameraManager.featureStoredLookAt.y, CameraManager.featureStoredLookAt.z, true);
    
    CameraManager.controls.enabled = true;
    ScenesManager.inFeature = false;
    ScenesManager.currentFeatureName = '';
    for (let key in ScenesManager.featureTravelingParticles) {
      if (ScenesManager.featureTravelingParticles[key]) {
        ScenesManager.featureTravelingParticles[key].targets.forEach(item => {
          const shouldShow = (!ScenesManager.inInterior && !item.userData.isInterior && !ScenesManager.EXTERNAL_REVEALEAD) || (ScenesManager.inInterior && item.userData.isInterior && !ScenesManager.INTERNAL_REVEALEAD);
        if (shouldShow) {
            gsap.to((item.material as ShaderMaterial).uniforms.uOpacity, {
              value: 1.0,
              ease: "quad.inOut",
              duration: 0.8,
              onStartt: () => {
                item.visible = true
              }
            });
          }
        })
      }
    }

    if (isMobile()) {
      CameraManager.toggleTitle(1);
    }

  }

  static async goToInterior(onComplete: () => void) {
    const interiorPosition = ScenesManager.lastAddedFeature?.name === "telas" ? CameraManager.interiorPositionTelas : CameraManager.interiorPosition;
    const interiorTarget = ScenesManager.lastAddedFeature?.name === "telas" ? CameraManager.interiorTargetTelas : CameraManager.interiorTarget;

    ScenesManager.inInterior = true;
    CameraManager.getCurrentPosAndTarget();
    CameraManager.controls.polarRotateSpeed = CameraManager.interiorRotateSpeed
    CameraManager.controls.azimuthRotateSpeed = CameraManager.interiorRotateSpeed
    CameraManager.controls.minDistance = 0.8;
    CameraManager.controls.maxDistance = 3.5;
    CameraManager.controls.setLookAt(
      interiorPosition.x, interiorPosition.y, interiorPosition.z,
      interiorTarget.x, interiorTarget.y, interiorTarget.z,
      true,
    );
    ScenesManager.inFeature = false;
    CameraManager.setEnable(true);
    await delay(0.5);
    CameraManager.controls.maxPolarAngle = Math.PI * 2;
    onComplete && onComplete();
  }
  
  static async goFromInterior() {
    CameraManager.controls.maxPolarAngle = 1.6652097969841715;
    // CameraManager.goToInitial();
    if (isMobile()) {
      await CameraManager.controls.setLookAt(
        5.865342412895533, 2.080469495261985, 12.728587565985617 ,
        -0.1752815410761034, -0.07006796836563967, 0,
        true,
      );
    } else {
      await CameraManager.controls.setLookAt(
        2.8067791506598523, 1.6357862148623452, 5.925702540999585 ,
        0, 0, 0,  
        true,
      );
    }
    // CameraManager.controls.setPosition(CameraManager.featureStoredPosition.x, CameraManager.featureStoredPosition.y, CameraManager.featureStoredPosition.z, true);
    // await CameraManager.controls.setTarget(CameraManager.featureStoredLookAt.x, CameraManager.featureStoredLookAt.y, CameraManager.featureStoredLookAt.z, true);
    ScenesManager.inInterior = false;

    CameraManager.controls.minDistance = isDesktop() ? 4 : 9;
    CameraManager.controls.maxDistance = isDesktop() ? 8 : 14;

    CameraManager.controls.polarRotateSpeed = 1.0
    CameraManager.controls.azimuthRotateSpeed = 1.0
  }

  static goToFeatureByFit(target: Mesh) {
    if (!target) return;
    CameraManager.getCurrentPosAndTarget();
    CameraManager.controls.fitToBox(target, true, {paddingBottom: 0.5, paddingLeft: 0.5, paddingRight: 0.5, paddingTop: 0.5})
  }

  static getCurrentPosAndTarget(shouldLog?:boolean){
    CameraManager.controls.getPosition(CameraManager.featureStoredPosition);
    CameraManager.controls.getTarget(CameraManager.featureStoredLookAt)
    if (shouldLog) {
      console.log(
        `featureStoredPosition: ${CameraManager.featureStoredPosition.x}, ${CameraManager.featureStoredPosition.y}, ${CameraManager.featureStoredPosition.z}`, 
        `\n\nfeatureStoredLookAt: ${CameraManager.featureStoredLookAt.x}, ${CameraManager.featureStoredLookAt.y}, ${CameraManager.featureStoredLookAt.z}`,
        `\n\nazimuthAngle: ${CameraManager.controls.azimuthAngle}`,
        `\n\npolarAngle: ${CameraManager.controls.polarAngle}`);
    }
  }

  static update(delta: number) {
    CameraManager.controls?.update(CameraManager.playingIntro ? delta * 0.3 : delta );
  }

  static dispose() {
    CameraManager.dispose();
  }
}
